import React from "react";
import { Box, Typography, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const EFFECTIVE_DATE = "July 1, 2026";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box mb={4}>
    <Typography variant="h5" component="h2" gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const TermsOfServicePage: React.FC = () => {
  return (
    <Box maxWidth="900px" margin="0 auto" padding="16px">
      <Typography variant="h4" component="h1" gutterBottom>
        Terms of Service
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Effective date: {EFFECTIVE_DATE}
      </Typography>

      <Section title="1. Who We Are">
        <Typography variant="body1" paragraph>
          Family Tree &amp; Me ("we," "us," or "our") creates custom family
          tree artwork, missionary photo art, and generational art pieces. We
          are located in Orem, Utah. You can reach us at{" "}
          <Link href="mailto:shirlenedymock@mac.com">
            shirlenedymock@mac.com
          </Link>{" "}
          or 801-400-5802.
        </Typography>
        <Typography variant="body1" paragraph>
          By creating an account, placing an order, or otherwise using this
          website (the "Service"), you agree to these Terms of Service. If you
          do not agree, please do not use the Service.
        </Typography>
      </Section>

      <Section title="2. Your Account">
        <Typography variant="body1" paragraph>
          You must provide accurate information when creating an account and
          keep your login credentials secure. You are responsible for all
          activity that occurs under your account. You must be at least 18
          years old, or use the Service under the supervision of a parent or
          guardian who agrees to these terms.
        </Typography>
      </Section>

      <Section title="3. Custom Orders">
        <Typography variant="body1" paragraph>
          Every art piece we sell is custom-made from the information and
          photographs you provide. After you place an order, you will be asked
          to complete the required forms, build your family tree, and upload
          photographs through your account dashboard. Production timelines
          begin only after we receive all required materials.
        </Typography>
        <Typography variant="body1" paragraph>
          Before printing, we will send you a digital proof for review. Please
          examine it carefully — names, dates, spelling, and photo placement
          are your responsibility to verify. Once you approve the proof, the
          piece goes to print, and errors you approved cannot be corrected at
          our expense.
        </Typography>
      </Section>

      <Section title="4. Refunds and Cancellations">
        <Typography variant="body1" paragraph>
          Because our products are custom-made:
        </Typography>
        <Typography variant="body1" component="ul" sx={{ pl: 3 }} paragraph>
          <li>
            You may cancel for a <strong>full refund at any time before
            design work begins</strong> on your order.
          </li>
          <li>
            <strong>Once design or production work has started, orders are
            non-refundable</strong> and cannot be exchanged.
          </li>
          <li>
            If we make an error — such as a typo we introduced, using the
            wrong photograph, or a printing defect — we will{" "}
            <strong>correct and reprint the piece at no charge</strong>.
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          If your artwork arrives damaged, contact us immediately and keep all
          packaging so a shipping claim can be filed. See our{" "}
          <Link component={RouterLink} to="/policies">
            shipping and handling policies
          </Link>{" "}
          for details.
        </Typography>
      </Section>

      <Section title="5. Payments">
        <Typography variant="body1" paragraph>
          Payments are processed by PayPal. You do not need a PayPal account to
          pay with a credit or debit card. Prices are stated in U.S. dollars.
          Sales tax is added for orders shipped within Utah. We never see or
          store your full card details.
        </Typography>
      </Section>

      <Section title="6. Your Content and Photographs">
        <Typography variant="body1" paragraph>
          You retain ownership of the photographs, names, dates, and family
          information you upload ("Your Content"). By uploading Your Content,
          you grant us a limited license to store, reproduce, and modify it
          solely to design, produce, and deliver your order and to provide the
          Service.
        </Typography>
        <Typography variant="body1" paragraph>
          You represent that you have the right to use and share every
          photograph and piece of information you upload, including the
          consent of living people who appear in your photos where required.
          You agree not to upload content that is unlawful or infringes
          someone else's rights.
        </Typography>
        <Typography variant="body1" paragraph>
          We will not use your photographs or family information for
          advertising without your written permission.
        </Typography>
      </Section>

      <Section title="7. Our Artwork">
        <Typography variant="body1" paragraph>
          The designs, layouts, and artistic elements we create — apart from
          Your Content — remain our intellectual property. Your purchase gives
          you the finished physical artwork for personal, non-commercial use.
          You may not reproduce our designs for resale.
        </Typography>
      </Section>

      <Section title="8. Acceptable Use">
        <Typography variant="body1" paragraph>
          You agree not to misuse the Service, including attempting to access
          other customers' data, interfering with the Service's operation, or
          using it for any unlawful purpose. We may suspend or close accounts
          that violate these terms.
        </Typography>
      </Section>

      <Section title="9. Disclaimers and Limitation of Liability">
        <Typography variant="body1" paragraph>
          The Service is provided "as is." To the fullest extent permitted by
          law, we disclaim all warranties not expressly stated in these terms,
          and our total liability for any claim arising from an order is
          limited to the amount you paid for that order. We are not liable for
          indirect, incidental, or consequential damages, including loss of
          data. Nothing in these terms limits liability that cannot be limited
          under applicable law.
        </Typography>
      </Section>

      <Section title="10. Governing Law">
        <Typography variant="body1" paragraph>
          These terms are governed by the laws of the State of Utah, without
          regard to its conflict-of-law rules. Any dispute will be resolved in
          the state or federal courts located in Utah County, Utah, and you
          consent to their jurisdiction.
        </Typography>
      </Section>

      <Section title="11. Changes to These Terms">
        <Typography variant="body1" paragraph>
          We may update these terms from time to time. The effective date at
          the top of this page shows when they were last revised. Material
          changes will be communicated through the Service or by email.
          Continued use of the Service after changes take effect constitutes
          acceptance of the revised terms.
        </Typography>
      </Section>

      <Section title="12. Contact">
        <Typography variant="body1" paragraph>
          Questions about these terms? Contact us at{" "}
          <Link href="mailto:shirlenedymock@mac.com">
            shirlenedymock@mac.com
          </Link>{" "}
          or 801-400-5802.
        </Typography>
      </Section>
    </Box>
  );
};

export default TermsOfServicePage;
